import React from 'react';

import { Cancel, Search } from '@mui/icons-material';
import { DatePicker } from '@mui/lab';
import { IconButton, InputAdornment, TextField, Tooltip } from '@mui/material';
import { type Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useContainer } from 'unstated-next';

import {
    DateContainer,
    DateRangeContainer,
    FilterCount,
    InputContainer,
    Label,
    Parent,
    StyledTextField
} from './styles';
import ClassSessionsContainer from '../ClassSessionsContainer';

interface ClassFiltersInterface {
    setPage: React.Dispatch<React.SetStateAction<number>>;
}

const ClassFilters: React.FC<ClassFiltersInterface> = ({ setPage }) => {
    const { t } = useTranslation('portal');

    const {
        filteredClassSessions,
        selectedInstructor,
        setSelectedInstructor,
        searchValue,
        setSearchValue,
        startDate,
        setStartDate,
        endDate,
        setEndDate
    } = useContainer(ClassSessionsContainer);

    const clearAllFilters = () => {
        setPage(1);
        setSearchValue('');
        setSelectedInstructor(null);
        setStartDate(null);
        setEndDate(null);
    };

    const onSearchChange = (value: string) => {
        setPage(1);
        setSearchValue(value);
    };

    const onStartDateChange = (date: Dayjs | null) => {
        setStartDate(date);
    };

    const onEndDateChange = (date: Dayjs | null) => {
        setEndDate(date);
    };

    const returnFilterCount = () => {
        const hasFilters = searchValue !== '' || selectedInstructor || startDate || endDate;

        if (hasFilters) {
            const returnText = (count: number): string => {
                switch (count) {
                    case 0:
                        return t('classes-page.no-classes-found');
                    case 1:
                        return t('classes-page.showing-1-results');
                    default:
                        return t('classes-page.showing-results-with-count', { count });
                }
            };

            return <FilterCount>{returnText(filteredClassSessions.length)}</FilterCount>;
        }

        return null;
    };

    return (
        <>
            <Parent>
                <InputContainer>
                    <Label htmlFor="search">{t('search-placeholder')}</Label>
                    <StyledTextField
                        value={searchValue}
                        onChange={(e) => onSearchChange(e.target.value)}
                        sx={{ width: 375 }}
                        size="small"
                        id="search"
                        name="search"
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <Search />
                                </InputAdornment>
                            )
                        }}
                    />
                </InputContainer>

                <DateRangeContainer>
                    <DateContainer>
                        <Label htmlFor="from">{t('classes-page.from')}</Label>
                        <DatePicker
                            value={startDate}
                            onChange={(date: Dayjs | null) => onStartDateChange(date)}
                            renderInput={(params) => (
                                <TextField size="small" sx={{ width: 200 }} id="from" name="from" {...params} />
                            )}
                        />
                    </DateContainer>
                    <DateContainer>
                        <Label htmlFor="to">{t('classes-page.to')}</Label>
                        <DatePicker
                            value={endDate}
                            onChange={(date: Dayjs | null) => onEndDateChange(date)}
                            renderInput={(params) => (
                                <TextField size="small" sx={{ width: 200 }} id="to" name="to" {...params} />
                            )}
                        />
                    </DateContainer>
                    <DateContainer>
                        <Tooltip arrow title={t('classes-page.clear-selection')}>
                            <IconButton onClick={clearAllFilters}>
                                <Cancel />
                            </IconButton>
                        </Tooltip>
                    </DateContainer>
                </DateRangeContainer>
            </Parent>
            {returnFilterCount()}
        </>
    );
};

export default ClassFilters;
